import { Box } from '@mui/material';
import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ComposableDashboardFiltersEnum, IComposableAnalyticsGlobalFilters } from 'wisestep-api';
import {
  ScrollableButtonTabs,
  WsColors,
  WsPageHeader,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { AccessControl, PinDashboardCollectionButton } from '../../../components';
import { WsBreadCrumbs } from '../../../components/WsBreadCrumbs';
import { isTeamMember } from '../../../features/jobs/listing/components/jobCardFooter/PostedBy';
import UserProfileOverview from '../../../features/recruiterProfileDetails/components/userProfile/UserProfileOverview';
import {
  getDashBoardBreadCrumbList,
  IDashBoardBreadCrumbTypes,
} from '../../../features/users/helper/defaultValues';
import AllCommunicationsLog from '../../conversationWidget/components/CommunicationsLog';

import TeamsUsersTab from './TeamsUsersTab';

const TeamMemberAndAnalyticsView = () => {
  const [param] = useSearchParams();
  const teamId = param.get('teamId') ?? '';
  const teamName = param.get('teamName') ?? '';
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [hasAccess, setHasAccess] = useState<boolean>(false);
  const getFilters = useMemo(() => {
    const filters = {} as Record<ComposableDashboardFiltersEnum, IComposableAnalyticsGlobalFilters>;
    filters[ComposableDashboardFiltersEnum.TEAM] = { teamIds: [teamId] };
    return filters;
  }, [teamId]);

  const tabs = [
    {
      tabName: 'Analytics',
    },
    {
      tabName: 'Users',
    },
    {
      tabName: 'Communications',
    },
  ];

  useEffect(() => {
    const checkAccess = async () => {
      if (teamId) {
        const access = await isTeamMember(teamId);
        setHasAccess(access ?? false);
      }
    };
    checkAccess();
  }, [teamId]);

  return (
    <>
      <Box mb={1.5}>
        <WsBreadCrumbs
          breadCrumbsList={getDashBoardBreadCrumbList(
            IDashBoardBreadCrumbTypes.TEAMS,
            teamName ?? ''
          )}
        />

        <WsPageHeader
          title={teamName ?? ''}
          profileImg={{ profileImage: undefined, required: true }}
          rightChildren={
            <PinDashboardCollectionButton
              filters={getFilters}
              entityId={teamId}
              entityName={teamName}
            />
          }
        />
      </Box>
      <Box borderRadius={2} sx={{ background: WsColors.White }} mb={1.5}>
        <ScrollableButtonTabs
          scrollList={tabs}
          getTabIndex={(index: number) => setTabIndex(index)}
        />
      </Box>
      <Box
        sx={{
          bgcolor: WsColors.White,
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12)',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {hasAccess ? (
          <>
            {tabIndex == 0 && <UserProfileOverview tabIndex={tabIndex} teamIds={[teamId ?? '']} />}
            {tabIndex == 1 && (
              <Box>
                <Box sx={{ ml: 4, mt: 3, mb: -2 }}>
                  <WsTypography variant={WsTypoVariants.TITLE} bold>
                    Users in Teams
                  </WsTypography>
                </Box>
                <TeamsUsersTab teamId={teamId} />
              </Box>
            )}
            {tabIndex == 2 && (
              <Box p={1}>
                <AllCommunicationsLog type="TEAM" entityId={teamId} />
              </Box>
            )}
          </>
        ) : (
          <AccessControl errorText="You don't have access to view this Team Analytics" />
        )}
      </Box>
    </>
  );
};

export default TeamMemberAndAnalyticsView;
